import { useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'

export type FeedItem = NonNullable<
  ResultOf<typeof GetFeedItems>['feedItems']['edges']
>[number]['node']

export const GetFeedItems = graphql(/* GraphQL */ `
  query GetFeedItems(
    $feedCollectionIds: [FeedCollectionId!]
    $feedIds: [FeedId!]
    $organizationId: OrganizationId!
    $after: String
  ) {
    feedItems(
      first: 20
      after: $after
      input: {
        organizationId: $organizationId
        filter: { feedCollectionIds: $feedCollectionIds, feedIds: $feedIds }
      }
    ) {
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
      edges {
        node {
          id
          createdAt
          title
          contentTextPreview
          articleUrl
          feed {
            id
            type
            name
            connectionMetadata {
              ... on RSSFeedConnectionMetadata {
                __typename
                url
              }
            }
          }
        }
      }
    }
  }
`)

type UseFeedItemsInput = {
  organizationId?: string | null
  feedCollectionIds?: string[]
  feedIds?: string[]
}

type UseFeedCollectionItemResult = {
  loading: boolean
  error?: ErrorLike
  items: FeedItem[] | null
  fetchingMore: boolean
  refetch: useQuery.Result<
    ResultOf<typeof GetFeedItems>,
    VariablesOf<typeof GetFeedItems>
  >['refetch']
  lastElementRef: (node: HTMLElement | null) => void
}

export const useFeedItems = (
  input: UseFeedItemsInput,
): UseFeedCollectionItemResult => {
  const { organizationId, feedCollectionIds, feedIds } = input

  const { data, error, fetchMore, loading, networkStatus, refetch } = useQuery<
    ResultOf<typeof GetFeedItems>,
    VariablesOf<typeof GetFeedItems>
  >(GetFeedItems, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      organizationId: organizationId!,
      feedCollectionIds,
      feedIds,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !organizationId,
  })

  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  const items = useMemo(() => {
    return data?.feedItems?.edges?.map((edge) => edge.node) ?? null
  }, [data?.feedItems?.edges])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.feedItems.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: {
            after: data?.feedItems.pageInfo.endCursor,
          },
        })
      } catch (fetchMoreFeedItemsError) {
        toast.error('Error loading more feed items')
        const errorMessage =
          fetchMoreFeedItemsError instanceof Error
            ? fetchMoreFeedItemsError.message
            : String(fetchMoreFeedItemsError)
        throw new Error(`Failed to fetch more feed items: ${errorMessage}`, {
          cause: fetchMoreFeedItemsError,
        })
      }
    },
  })

  return {
    loading,
    lastElementRef,
    fetchingMore,
    error,
    items,
    refetch,
  }
}
